import {
  CollisionGrid,
  ZONE_OCCUPANCY_INTERVAL_MS,
  zoneAt,
  zoneOccupancy,
  type AvatarConfig,
  type Direction,
  type MapData,
  type MapObject,
  type PlayerState,
  type PositionUpdate,
  type Zone,
  yawFromDirection,
} from '@etown/shared';
import { resolveAvatar } from '../lib/avatarParts';
import { isSegmentClear } from '../lib/lineOfSight';
import { findPath, nearestWalkableTile, smoothPath, type PathGrid } from '../lib/pathfinding';
import { panForPeer } from '../webrtc/panning';
import type { IWorldRenderer } from '../renderer/IWorldRenderer';
import type { AvatarRenderState } from '../renderer/renderTypes';
import { AutoWalk } from './AutoWalk';
import { GameLoop } from './GameLoop';
import type { IInputController } from './IInputController';
import { LocalPlayer, type SendMove } from './LocalPlayer';
import { OverlayProjector } from './OverlayProjector';
import { ProximityHints } from './ProximityHints';
import { RemotePlayers } from './RemotePlayers';
import { istStuhl, sitzMitte, type SitzAktion, type Sitzplatz } from './sitzen';
import type { ViewBundle, ViewFactory, ViewMode, ViewSettings } from './view';

export interface GameRuntimeOptions {
  host: HTMLElement;
  /** Startansicht; zur Laufzeit ueber setViewMode wechselbar. */
  viewMode: ViewMode;
  /** Baut Renderer und Steuerung — haelt die Spiellogik technikfrei. */
  createView: ViewFactory;
  map: MapData;
  selfId: string;
  selfDisplayName: string;
  selfAvatarColor: number;
  selfAvatar: AvatarConfig | null;
  selfRingRequired: boolean;
  initialPlayers: PlayerState[];
  sendMove: SendMove;
  onInteract: (obj: MapObject) => void;
  /** Taste C: den uebergebenen (naechsten anklingelbaren) Peer anklingeln. */
  onRing: (peerId: string) => void;
  /** IDs der Peers, die aktuell angeklingelt werden koennen (pending, nicht klingelnd). */
  getRingablePeerIds: () => string[];
  onNearbyObject: (obj: MapObject | null) => void;
  /** Was die Benutzen-Taste am Stuhl bewirkt; null = kein Stuhl im Spiel. */
  onSitzen: (aktion: SitzAktion | null) => void;
  onZoneChange: (zone: Zone | null) => void;
  onPeerAudio: (peerId: string, cue: { distanceTiles: number; pan: number; occluded: boolean }) => void;
  /** Belegung der Zonen (fuer Konferenz-Banner); throttled + dirty-gecheckt. */
  onZoneOccupancy: (occupancy: ReadonlyMap<string, ReadonlyArray<{ id: string; displayName: string }>>) => void;
  /** Peers, mit denen aktuell eine WebRTC-Verbindung besteht. */
  getConnectedPeerIds: () => string[];
  /** Peers, die gerade hoerbar senden (Sprech-Ring am Avatar). */
  getSpeakingPeerIds: () => ReadonlySet<string>;
}

/**
 * Verdrahtet alle Spiel-Bausteine (Loop, Input, Spieler, Renderer, Overlay)
 * zu einer Einheit mit schmaler API fuer React. Kennt nur die Interfaces der
 * Ansicht, nicht deren Technik. Erzeugung ist async, weil der Renderer seinen
 * Kontext aufbauen muss.
 */
export class GameRuntime {
  readonly projector = new OverlayProjector();
  private renderer: IWorldRenderer;
  private input: IInputController;
  private viewMode: ViewMode;
  private switching = false;
  private loop: GameLoop;
  private localPlayer: LocalPlayer;
  private remotePlayers = new RemotePlayers();
  private hints: ProximityHints;
  private map: MapData;
  private nearbyObject: MapObject | null = null;
  /** Stuhl in Reichweite (null = keiner). */
  private stuhl: Sitzplatz | null = null;
  /** Zuletzt gemeldeter Zustand — die Oberflaeche soll nicht doppelt hoeren. */
  private gemeldetesObjekt: string | null = null;
  private gemeldeteSitzAktion: SitzAktion | null = null;
  private destroyed = false;
  private selfAvatar: AvatarConfig | null;
  private selfRingRequired: boolean;
  private occupancyTimerMs = 0;
  private occupancyKey = '';
  private deniedZoneIds = new Set<string>();
  private collision: CollisionGrid;
  private autoWalk = new AutoWalk();
  /** Zuletzt gesetzte Objekt-Inhalte — nach einem Ansichtswechsel neu einspielen. */
  private objectImages = new Map<string, string>();
  private objectBoards = new Map<string, HTMLCanvasElement>();
  /** Zuletzt gesetzte Einstellungen — die neue Ansicht braucht sie ebenfalls. */
  private viewSettings: ViewSettings | null = null;

  private constructor(
    private readonly opts: GameRuntimeOptions,
    view: ViewBundle,
  ) {
    this.viewMode = opts.viewMode;
    this.renderer = view.renderer;
    this.input = view.input;
    this.map = opts.map;
    this.selfAvatar = opts.selfAvatar;
    this.selfRingRequired = opts.selfRingRequired;
    const spawn = opts.map.spawns[0] ?? { id: 'fallback', x: 2, y: 2, dir: 'down' as Direction };
    this.collision = CollisionGrid.fromMapData(opts.map);
    this.localPlayer = new LocalPlayer(spawn, this.collision, opts.sendMove);
    this.localPlayer.setAccessCheck((x, y) => this.mayEnter(x, y));

    this.hints = new ProximityHints(opts.map, {
      onNearbyObject: (obj) => {
        this.nearbyObject = obj;
        this.zielAktualisieren();
      },
      onNearbyChair: (platz) => {
        this.stuhl = platz;
        this.zielAktualisieren();
      },
      onZoneChange: opts.onZoneChange,
      onPeerDistance: (peerId, distanceTiles, peer) =>
        opts.onPeerAudio(peerId, {
          distanceTiles,
          pan: panForPeer(this.localPlayer.x, this.localPlayer.y, peer.x, peer.y, this.localPlayer.yaw),
          // Ansichten ohne Verdeckung fuehren die Methode nicht — die
          // Draufsicht daempft Stimmen hinter Waenden also nicht
          occluded: this.renderer.projection.occludes?.(peer.x, peer.y) ?? false,
        }),
    });

    for (const player of opts.initialPlayers) {
      if (player.id !== opts.selfId) this.remotePlayers.upsert(player, performance.now());
    }

    this.bindInput();

    this.loop = new GameLoop(
      (dtMs, nowMs) => this.updateStep(dtMs, nowMs),
      (nowMs) => this.renderStep(nowMs),
    );
  }

  static async create(opts: GameRuntimeOptions): Promise<GameRuntime> {
    // Die Ego-Ansicht beginnt mit dem Blick des Spawns — im Stadion nach
    // Westen aufs Marathontor, nicht stur nach Norden
    const spawn = opts.map.spawns[0];
    const startYaw = yawFromDirection(spawn?.dir ?? 'down');
    const runtime = new GameRuntime(opts, await opts.createView(opts.viewMode, opts.map, startYaw));
    if (opts.viewMode === '3d') runtime.localPlayer.setYaw(startYaw);
    await runtime.renderer.mount(opts.host);
    runtime.renderer.setMap(opts.map);
    runtime.input.attach(opts.host);
    runtime.loop.start();
    return runtime;
  }

  /** Callbacks an die aktuelle Steuerung haengen. */
  private bindInput(): void {
    // Eine Taste, drei Bedeutungen — in dieser Reihenfolge: Wer sitzt, steht
    // auf (sonst kaeme er nicht mehr weg); sonst gilt das Objekt im
    // Fadenkreuz; und wenn dort keins ist, der Stuhl davor.
    this.input.onInteract(() => this.benutzen());
    this.input.onRing(() => this.anklingeln());
    // Zeiger-Auswahl -> Ziel laufen. Die Umrechnung laeuft ueber die aktive
    // Projektion, der Input kennt nur Bildschirmkoordinaten.
    this.input.onPick((screenX, screenY) => {
      const world = this.renderer.projection.screenToWorld(screenX, screenY);
      this.walkTo(Math.floor(world.x), Math.floor(world.y));
    });
  }

  /**
   * Die Benutzen-Taste, auch fuer Bildschirmknoepfe: Wer sitzt, steht auf;
   * sonst gilt das Objekt im Fadenkreuz; sonst der Stuhl davor.
   */
  benutzen(): void {
    if (this.localPlayer.sitzt) {
      this.aufstehen();
      return;
    }
    if (this.nearbyObject) {
      this.opts.onInteract(this.nearbyObject);
      return;
    }
    if (this.stuhl) this.hinsetzen(this.stuhl);
  }

  /** Den naechsten anklingelbaren Peer anklingeln. */
  anklingeln(): void {
    const peerId = this.nearestRingablePeer();
    if (peerId) this.opts.onRing(peerId);
  }

  currentViewMode(): ViewMode {
    return this.viewMode;
  }

  /**
   * Ansicht zur Laufzeit wechseln. Spielzustand, Socket und Overlay-Anker
   * bleiben bestehen — nur Renderer und Steuerung werden getauscht, der alte
   * Renderer gibt seinen Kontext vorher vollstaendig frei.
   */
  async setViewMode(mode: ViewMode): Promise<void> {
    if (mode === this.viewMode || this.switching || this.destroyed) return;
    await this.ansichtTauschen(mode);
  }

  /**
   * Ansicht im selben Modus neu aufbauen — noetig, wenn der 3D-Stil
   * wechselt. Welcher es sein soll, liest `createView` beim Aufbau aus dem
   * `viewStore`; hier wird nur getauscht, wie beim Moduswechsel auch.
   *
   * Der Zeigerfang geht dabei verloren: `detach` gibt ihn frei, und
   * zurueckholen liesse er sich nur mit einer frischen Geste des Nutzers.
   * Beim Bedienen des Popovers ist er ohnehin schon frei.
   */
  async ansichtNeuAufbauen(): Promise<void> {
    if (this.switching || this.destroyed) return;
    await this.ansichtTauschen(this.viewMode);
  }

  /** Renderer und Steuerung austauschen — gemeinsamer Kern beider Wechsel. */
  private async ansichtTauschen(mode: ViewMode): Promise<void> {
    this.switching = true;
    try {
      const blocked = this.input.blocked;
      // Die alte Projektion ist gleich weg — Overlays wuerden sonst bis zum
      // Ende des Wechsels an ihrer letzten Position kleben
      this.projector.hideAll();
      this.input.detach();
      this.renderer.destroy();

      const view = await this.opts.createView(mode, this.map, this.localPlayer.yawOrFacing());
      // Der Nachbau der Ansicht laedt nach; wurde die Runtime waehrenddessen
      // beendet, darf die frische Ansicht nicht mehr angehaengt werden
      if (this.destroyed) {
        view.renderer.destroy();
        return;
      }
      this.renderer = view.renderer;
      this.input = view.input;
      this.input.blocked = blocked;
      this.viewMode = mode;

      await this.renderer.mount(this.opts.host);
      this.renderer.setMap(this.map);
      this.renderer.setDeniedZones(this.deniedZoneIds);
      // Die neue Ansicht kennt Einstellungen und Objekt-Inhalte noch nicht
      if (this.viewSettings) {
        this.renderer.applySettings(this.viewSettings);
        this.input.applySettings(this.viewSettings);
      }
      for (const [objectId, imageId] of this.objectImages) this.renderer.setObjectImage(objectId, imageId);
      for (const [objectId, canvas] of this.objectBoards) this.renderer.setObjectBoard(objectId, canvas);
      // Das Objekt im Fadenkreuz leuchtet erst wieder, wenn es der frischen
      // Ansicht auch gesagt wird — von selbst weiss sie nichts davon
      this.renderer.setObjectHighlight(this.gemeldetesObjekt);
      // Die Belegung der Raumschilder haengt an einem Dirty-Check; ohne
      // dieses Zuruecksetzen blieben die Schilder der frischen Ansicht leer,
      // bis das naechste Mal jemand einen Raum betritt oder verlaesst
      this.occupancyKey = '';
      this.bindInput();
      this.input.attach(this.opts.host);
      // Die Draufsicht kennt keinen Blickwinkel — dann faellt yaw wieder weg
      this.localPlayer.setYaw(mode === '3d' ? this.localPlayer.yawOrFacing() : null);
      this.autoWalk.cancel();
    } finally {
      this.switching = false;
    }
  }

  /** Auf den Stuhl setzen: Fusspunkt auf die Kachelmitte, Beine ruhen. */
  private hinsetzen(platz: Sitzplatz): void {
    this.autoWalk.cancel();
    const mitte = sitzMitte(platz);
    this.localPlayer.hinsetzen(mitte.x, mitte.y);
    this.zielAktualisieren();
  }

  private aufstehen(): void {
    this.localPlayer.aufstehen();
    this.zielAktualisieren();
  }

  /**
   * Was die Benutzen-Taste gerade bewirkt, an Renderer und Oberflaeche
   * melden — aber nur, wenn es sich geaendert hat.
   *
   * Im Sitzen gehoert die Taste dem Aufstehen. Ein Objekt-Hinweis wuerde dann
   * etwas versprechen, was sie nicht tut, und bleibt darum stumm.
   */
  private zielAktualisieren(): void {
    const sitzt = this.localPlayer.sitzt;
    const objekt = sitzt ? null : this.nearbyObject;
    const sitzAktion: SitzAktion | null = sitzt ? 'aufstehen' : this.stuhl ? 'hinsetzen' : null;

    if ((objekt?.id ?? null) !== this.gemeldetesObjekt) {
      this.gemeldetesObjekt = objekt?.id ?? null;
      // Waehrend des Ansichtswechsels lebt kein Renderer; der Umbau spielt
      // die Hervorhebung am Ende selbst wieder ein
      if (!this.switching) this.renderer.setObjectHighlight(this.gemeldetesObjekt);
      this.opts.onNearbyObject(objekt);
    }
    if (sitzAktion !== this.gemeldeteSitzAktion) {
      this.gemeldeteSitzAktion = sitzAktion;
      this.opts.onSitzen(sitzAktion);
    }
  }

  /** Pfad zum Zielfeld berechnen und den Auto-Walk starten. */
  private walkTo(tileX: number, tileY: number): void {
    // Im Sitzen laeuft auch niemand einem Mausklick hinterher
    if (this.localPlayer.sitzt) return;
    const grid: PathGrid = {
      width: this.map.width,
      height: this.map.height,
      isBlocked: (x, y) => this.collision.isBlocked(x, y) || !this.mayEnter(x, y),
    };
    let goal = { x: tileX, y: tileY };
    if (grid.isBlocked(goal.x, goal.y)) {
      const near = nearestWalkableTile(grid, goal, 2);
      if (!near) return;
      goal = near;
    }
    const start = { x: Math.floor(this.localPlayer.x), y: Math.floor(this.localPlayer.y) };
    const path = findPath(grid, start, goal);
    if (!path || path.length === 0) return;
    // Sichtlinien-Glaettung mit kontinuierlicher Begehbarkeit + Zutrittspruefung
    const walkable = (x: number, y: number): boolean => this.collision.isWalkable(x, y) && this.mayEnter(x, y);
    const waypoints = smoothPath(path, (ax, ay, bx, by) => isSegmentClear(ax, ay, bx, by, walkable)).map((p) => ({
      x: p.x + 0.5,
      y: p.y + 0.5,
    }));
    this.autoWalk.start(waypoints);
    this.renderer.showClickMarker(goal.x + 0.5, goal.y + 0.5);
  }

  private updateStep(dtMs: number, nowMs: number): void {
    // Blickwinkel der Ego-Ansicht uebernehmen (bestimmt dort auch die Pose)
    const look = this.input.look();
    if (look) this.localPlayer.setYaw(look.yaw);

    // Tastatur hat Vorrang und bricht einen laufenden Auto-Walk ab
    const keyVec = this.input.vector();
    if (keyVec.x !== 0 || keyVec.y !== 0) this.autoWalk.cancel();
    const vec = this.autoWalk.active ? this.autoWalk.vector(dtMs, this.localPlayer) : keyVec;
    this.localPlayer.update(dtMs, vec, nowMs);
    this.hints.update(
      dtMs,
      { x: this.localPlayer.x, y: this.localPlayer.y, yaw: this.localPlayer.yaw },
      this.remotePlayers.positions(),
      this.opts.getConnectedPeerIds(),
    );
    this.updateZoneOccupancy(dtMs);
  }

  /** Zonen-Belegung fuer Raumschilder + Konferenz-Banner (throttled). */
  private updateZoneOccupancy(dtMs: number): void {
    this.occupancyTimerMs += dtMs;
    if (this.occupancyTimerMs < ZONE_OCCUPANCY_INTERVAL_MS) return;
    this.occupancyTimerMs = 0;

    const players: Array<{ id: string; displayName: string; x: number; y: number }> = [
      { id: this.opts.selfId, displayName: this.opts.selfDisplayName, x: this.localPlayer.x, y: this.localPlayer.y },
    ];
    for (const [id, pos] of this.remotePlayers.positions()) {
      players.push({ id, displayName: pos.displayName, x: pos.x, y: pos.y });
    }
    const occupancy = zoneOccupancy(this.map.zones, players);

    // Dirty-Check ueber einen kompakten Schluessel — Text-Updates sind teuer
    const key = [...occupancy.entries()].map(([zoneId, list]) => `${zoneId}=${list.map((p) => p.id).join(',')}`).join(';');
    if (key === this.occupancyKey) return;
    this.occupancyKey = key;
    this.renderer.syncZoneSigns(occupancy);
    this.opts.onZoneOccupancy(occupancy);
  }

  private renderStep(nowMs: number): void {
    // Waehrend des Ansichtswechsels gibt es keinen lebenden Renderer
    if (this.switching) return;
    const speaking = this.opts.getSpeakingPeerIds();
    const selfState: AvatarRenderState = {
      id: this.opts.selfId,
      x: this.localPlayer.x,
      y: this.localPlayer.y,
      direction: this.localPlayer.direction,
      yaw: this.localPlayer.yaw ?? undefined,
      moving: this.localPlayer.moving,
      speaking: false,
      displayName: this.opts.selfDisplayName,
      avatar: resolveAvatar(this.selfAvatar, this.opts.selfAvatarColor),
      dnd: this.selfRingRequired,
      isSelf: true,
    };
    const states = [...this.remotePlayers.sample(nowMs, speaking), selfState];
    // Effekte zuerst: `applyCamera` zeichnet das Bild, also muss alles, was
    // dieses Bild betrifft — etwa das Flackern eines Themes — vorher stehen.
    this.renderer.updateEffects(nowMs);
    // Dann die Kamera, dann die Avatare — die Ego-Ansicht waehlt ihre
    // Billboard-Posen anhand des Kamerastandpunkts
    this.renderer.applyCamera({
      x: this.localPlayer.x,
      y: this.localPlayer.y,
      look: this.input.look(),
      moving: this.localPlayer.moving,
      seated: this.localPlayer.sitzt,
    });
    this.renderer.syncAvatars(states);

    // DOM-Overlay (Video-Bubbles etc.) an die Welt heften
    const posById = new Map<string, { x: number; y: number }>();
    for (const s of states) posById.set(s.id, { x: s.x, y: s.y });
    // Nur die Position aufloesen — ob eine Wand davor steht, entscheidet die
    // Projektion. Das Welt-Ziel traegt seine Koordinaten schon, es wird direkt
    // durchgereicht statt pro Frame neu verpackt.
    this.projector.project(this.renderer.projection, (target) =>
      target.kind === 'world' ? target : posById.get(target.id) ?? null,
    );
  }

  // --- API fuer die Netz-Anbindung ---

  applyServerUpdates(moves: readonly PositionUpdate[]): void {
    const now = performance.now();
    const selfUpdate = moves.find((m) => m.id === this.opts.selfId);
    if (selfUpdate) this.localPlayer.applyServerUpdate(selfUpdate);
    this.remotePlayers.addUpdates(moves.filter((m) => m.id !== this.opts.selfId), now);
  }

  addPlayer(player: PlayerState): void {
    if (player.id !== this.opts.selfId) this.remotePlayers.upsert(player, performance.now());
  }

  removePlayer(id: string): void {
    this.remotePlayers.remove(id);
  }

  /** Avatar-Aenderung eines Remote-Spielers (player:avatar). */
  updateAvatar(id: string, avatar: AvatarConfig): void {
    if (id === this.opts.selfId) this.selfAvatar = avatar;
    else this.remotePlayers.updateAvatar(id, avatar);
  }

  /** Eigene Avatar-Konfiguration nach dem Speichern im Editor. */
  setSelfAvatar(avatar: AvatarConfig): void {
    this.selfAvatar = avatar;
  }

  /** Avatar-Vorschau als Bild (nutzt den bestehenden Renderer, kein 2. Kontext). */
  snapshotAvatar(config: AvatarConfig): string {
    return this.renderer.snapshotAvatar(config);
  }

  /** Anklingel-Pflicht-Aenderung (player:updated bzw. eigene Einstellung). */
  setRingRequired(id: string, ringRequired: boolean): void {
    if (id === this.opts.selfId) this.selfRingRequired = ringRequired;
    else this.remotePlayers.setRingRequired(id, ringRequired);
  }

  /** Bild eines 'picture'-Objekts in der Welt setzen (Snapshot + object:image). */
  setObjectImage(objectId: string, imageId: string | null): void {
    if (imageId) this.objectImages.set(objectId, imageId);
    else this.objectImages.delete(objectId);
    this.renderer.setObjectImage(objectId, imageId);
  }

  /** Geraete-Einstellungen der Ego-Ansicht anwenden. */
  applyViewSettings(settings: ViewSettings): void {
    this.viewSettings = settings;
    // Waehrend eines Ansichtswechsels lebt kein Renderer. Das ist keine
    // graue Theorie: beim Stilwechsel steht das Popover offen, und wer
    // waehrenddessen an einem Regler zieht, traefe sonst den zerstoerten.
    // Gemerkt ist es oben — der Umbau spielt es am Ende selbst ein.
    if (this.switching) return;
    this.renderer.applySettings(settings);
    this.input.applySettings(settings);
  }

  /** Whiteboard-Inhalt an die Wand in der Welt legen. */
  setObjectBoard(objectId: string, canvas: HTMLCanvasElement | null): void {
    if (canvas) this.objectBoards.set(objectId, canvas);
    else this.objectBoards.delete(objectId);
    this.renderer.setObjectBoard(objectId, canvas);
  }

  /** Zutritts-Sperren aktualisieren (Snapshot + zone:access); markiert die Zonen. */
  setDeniedZones(ids: readonly string[]): void {
    this.deniedZoneIds = new Set(ids);
    this.renderer.setDeniedZones(this.deniedZoneIds);
    // Ein laufender Auto-Walk koennte jetzt durch einen gesperrten Raum fuehren
    this.autoWalk.cancel();
  }

  /** Praedikat fuer die lokale Zutrittspruefung (verbotene Zonen wie Waende). */
  private mayEnter(x: number, y: number): boolean {
    if (this.deniedZoneIds.size === 0) return true;
    const zone = zoneAt(this.map.zones, Math.floor(x), Math.floor(y));
    return !zone || !this.deniedZoneIds.has(zone.id);
  }

  /** Hot-Reload nach map:updated. */
  setMap(map: MapData): void {
    this.map = map;
    this.hints.setMap(map);
    this.renderer.setMap(map);
    this.autoWalk.cancel(); // Raster hat sich geaendert
    this.collision = CollisionGrid.fromMapData(map);
    this.localPlayer.setCollision(this.collision);
    // Wurde der Stuhl unter dem Sitzenden wegeditiert, steht er auf — sonst
    // klebte er auf einer Kachel fest, auf der nichts mehr steht
    if (this.localPlayer.sitzt && !stehtAufStuhl(map, this.localPlayer.x, this.localPlayer.y)) this.aufstehen();
    // Steht der Avatar jetzt in einer Wand, zurueck zum Spawn
    if (!this.collision.isWalkable(this.localPlayer.x, this.localPlayer.y)) {
      const spawn = map.spawns[0];
      if (spawn) {
        this.localPlayer.aufstehen();
        this.localPlayer.teleport(spawn.x, spawn.y);
        this.zielAktualisieren();
      }
    }
  }

  /** Modal offen -> Spielsteuerung pausieren. */
  setInputBlocked(blocked: boolean): void {
    this.input.blocked = blocked;
    if (blocked) this.autoWalk.cancel();
  }

  /** Zeichenlast der aktuellen Ansicht (Perf-Overlay). */
  renderStats(): { calls: number; triangles: number } | null {
    return this.renderer.renderStats();
  }

  /** Eigene Pose fuer die Minimap (Blickwinkel nur in der Ego-Ansicht). */
  selfPose(): { x: number; y: number; yaw: number | null } {
    return { x: this.localPlayer.x, y: this.localPlayer.y, yaw: this.localPlayer.yaw };
  }

  /** Letzte bekannte Positionen der anderen — fuer die Minimap. */
  peerPositions(): Array<{ id: string; x: number; y: number }> {
    return [...this.remotePlayers.positions()].map(([id, pos]) => ({ id, x: pos.x, y: pos.y }));
  }

  selfPosition(): { x: number; y: number } {
    return { x: this.localPlayer.x, y: this.localPlayer.y };
  }

  /** Naechster anklingelbarer Peer (Schnittmenge aus RTC-Kandidaten + Positionen). */
  private nearestRingablePeer(): string | null {
    const candidates = new Set(this.opts.getRingablePeerIds());
    if (candidates.size === 0) return null;
    const self = this.selfPosition();
    let best: string | null = null;
    let bestDist = Infinity;
    for (const [id, pos] of this.remotePlayers.positions()) {
      if (!candidates.has(id)) continue;
      const dist = Math.hypot(pos.x - self.x, pos.y - self.y);
      if (dist < bestDist) {
        bestDist = dist;
        best = id;
      }
    }
    return best;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.loop.stop();
    this.input.detach();
    this.renderer.destroy();
  }
}

/** Steht unter diesem Fusspunkt noch ein Stuhl? */
function stehtAufStuhl(map: MapData, x: number, y: number): boolean {
  return istStuhl(map.layers.decor[Math.floor(y)]?.[Math.floor(x)]);
}
